import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { getMsalInstance, loginRequest, initializeMsal } from "./auth/authConfig";
import { b2cPolicies } from "./auth/policies";
import NavTop from "./Components/NavTop.jsx";

// Lazy-loaded route components
const RSVPEventsPage = lazy(() => import("./Components/RSVPEventsPage.jsx"));
const ReportEmbed = lazy(() => import("./Components/ReportEmbed.jsx"));
const RSVPUpcomingEvents = lazy(() => import("./Components/RSVPUpcomingEvents.jsx"));
const RSVPRegisteredEvents = lazy(() => import("./Components/RSVPRegisteredEvents.jsx"));
const ContractDocs = lazy(() => import("./Components/ContractDocs.jsx"));
const Barcode = lazy(() => import("./Components/Barcode.jsx"));
const Home = lazy(() => import("./Components/Home.jsx"));
const DocViewer = lazy(() => import("./Components/DocViewer.jsx"));
const ExecutiveMinutes = lazy(() => import("./Components/ExecutiveMinutes.jsx"));

function App() {
  const [authAccount, setAuthAccount] = useState(() => {
    const stored = localStorage.getItem("authAccount");
    try {
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      localStorage.removeItem("authAccount");
      window.location.reload();
      return null;
    }
  });
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initialize = async () => {
      console.log("Initializing MSAL...");
      try {
        await initializeMsal();
        const msalInstance = getMsalInstance();

        let response = null;
        response = await msalInstance.handleRedirectPromise().catch(async (error) => {
          if (error?.errorMessage?.includes("AADB2C90118")) {
            console.warn("Forgot password triggered. Redirecting to password reset policy.");
            return msalInstance.loginRedirect({
              authority: b2cPolicies.authorities.forgotPassword.authority,
              scopes: ["openid"],
            });
          } else if (error?.errorMessage?.includes("AADB2C90091")) {
            console.warn("Password reset canceled. Redirecting back to sign-in policy.");
            return msalInstance.loginRedirect({
              ...loginRequest,
              authority: b2cPolicies.authorities.signUpSignIn.authority,
            });
          } else {
            console.error("Unhandled error during redirect handling:", error);
            throw error;
          }
        });
        console.log("handleRedirectPromise response:", response);

        const account = response?.account || msalInstance.getAllAccounts()[0];
        if (account) {
          console.log("Account detected:", account);
          setAuthAccount(account);
          msalInstance.setActiveAccount(account);
          localStorage.setItem("authAccount", JSON.stringify(account));
        } else {
          console.log("No account found. Redirecting to login.");
          await msalInstance.loginRedirect({
            ...loginRequest,
            authority: b2cPolicies.authorities.signUpSignIn.authority,
          });
        }
      } catch (err) {
        console.error("MSAL init error:", err);
        if (err.errorMessage?.includes("AADB2C90118")) {
          const msalInstance = getMsalInstance();
          return msalInstance.loginRedirect({
            authority: b2cPolicies.authorities.forgotpassword.authority,
            scopes: ["openid"],
          });
        }
        if (err.errorCode === "interaction_in_progress") {
          console.warn("Interaction already in progress.");
        } else if (err.errorCode === "endpoints_resolution_error") {
          console.error("Check your B2C authority config.");
        }
      } finally {
        setIsMsalInitialized(true);
      }
    };

    // Inject Freshdesk widget script if it doesn't already exist
    if (!document.querySelector('[data-testid="freshdesk-script"]')) {
      const script = document.createElement("script");
      script.src = "https://widget.freshworks.com/widgets/123456.js";
      script.async = true;
      script.setAttribute("data-testid", "freshdesk-script");
      document.head.appendChild(script);
    }

    initialize();
  }, []);

  const logout = async () => {
    try {
      const msalInstance = getMsalInstance();
      if (!msalInstance || !msalInstance.getActiveAccount()) {
        console.warn("Logout skipped: No active MSAL account or instance uninitialized.");
        return;
      }
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout Error:", error);
      if (error.errorCode === "uninitialized_public_client_application") {
        console.error("MSAL logout attempted before initialization. Ensure initializeMsal() completes first.");
      }
    }
  };

  if (!isMsalInitialized) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Loading SEIU Portal...</div>;
  }

  if (isMsalInitialized && !authAccount && localStorage.getItem("authAccount")) {
    localStorage.removeItem("authAccount");
    window.location.reload();
    return null;
  }

  if (!authAccount) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Authenticating user...</div>;
  }

  const memberID = authAccount?.idTokenClaims?.jobTitle || "Unknown Member";

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <NavTop memberID={memberID} logout={logout} />
      <Suspense fallback={<div style={{ padding: "2rem" }}>Loading page...</div>}>
        <Routes>
          <Route path="/" element={<Home memberID={memberID} />} />
          <Route path="/rsvpeventspage" element={<RSVPEventsPage memberID={memberID} />} />
          <Route
            path="/reportembed"
            element={
              <ReportEmbed
                memberID={memberID}
                embedInfo={null}
                embedAddress={null}
                embedReportID={null}
              />
            }
          />
          <Route path="/rsvpupcomingevents" element={<RSVPUpcomingEvents memberID={memberID} />} />
          <Route path="/rsvpregisteredevents" element={<RSVPRegisteredEvents memberID={memberID} />} />
          <Route path="/contractdocs" element={<ContractDocs memberID={memberID} />} />
          <Route path="/barcode" element={<Barcode />} />
          <Route path="/doc/*" element={<DocViewer />} />
          <Route path="/view" element={<DocViewer />} />
          <Route path="/executive-minutes" element={<ExecutiveMinutes />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;